/**
 * Auth bypass + mock session (prototyping / public demo) — NOT for real users.
 *
 * Seeds a fake session (dev@local.test) and installs the in-browser API mock so
 * the app is usable without Stytch or a backend. Activation is controlled by
 * isDevAuthBypass(): auto-on in `pnpm dev` when Stytch keys are placeholder, and
 * forced on/off anywhere (incl. production) via VITE_DEV_AUTH_BYPASS=true/false.
 *
 * With VITE_DEV_AUTH_BYPASS=true this also powers the public demo deploy.
 * ⚠️ That means a publicly reachable build has fake auth + mock data and NO real
 * authentication — only enable it for throwaway prototype/demo deployments.
 *
 * This file + the prototype mock are dev/demo scaffolding for the
 * thresholds-carried-forward-amounts prototype. To remove entirely: delete this
 * file + src/prototypes/thresholds-carried-forward-amounts/ and revert the
 * marked lines in main.tsx and useAuth.ts.
 */
import type { AuthTokens, Label, Organization, User } from "@/types";
import { tokenStorage } from "@/lib/auth";
import { installDevApiMock } from "@/prototypes/thresholds-carried-forward-amounts/mock";

function isPlaceholder(value: string): boolean {
  return value.trim() === "" || value.includes("xxx");
}

/**
 * Whether to bypass real auth and serve the in-browser mock.
 *
 *  - VITE_DEV_AUTH_BYPASS="true"  → ON in ANY build, including production. This
 *    powers the public demo deploy (fake auth + mock data, no backend needed).
 *  - VITE_DEV_AUTH_BYPASS="false" → OFF (use real Stytch login).
 *  - unset → auto-enable only in `pnpm dev` when Stytch keys are missing/placeholder.
 *
 * ⚠️ Setting the flag to "true" on a public deployment exposes a fake-auth demo
 * with mock data — there is NO real authentication. Use only for prototypes.
 */
export function isDevAuthBypass(): boolean {
  const flag = (import.meta.env.VITE_DEV_AUTH_BYPASS ?? "").toLowerCase();
  if (flag === "true") return true;
  if (flag === "false") return false;
  // No explicit flag: auto-enable only in dev with missing/placeholder Stytch keys.
  if (!import.meta.env.DEV) return false;
  const token = import.meta.env.VITE_STYTCH_PUBLIC_TOKEN ?? "";
  const org = import.meta.env.VITE_STYTCH_ORGANIZATION_ID ?? "";
  return isPlaceholder(token) || isPlaceholder(org);
}

const DEV_LABEL: Label = {
  id: "dev-label",
  name: "Dev Label",
  currency: "USD",
};

const DEV_ORG: Organization = {
  id: "dev-org",
  name: "Dev Organization",
  slug: "dev-org",
  labels: [DEV_LABEL],
  role: "owner",
};

/** Fake current user returned to the app while the bypass is active. */
export const DEV_USER: User = {
  id: "dev-user",
  username: "dev",
  email: "dev@local.test",
  first_name: "Dev",
  last_name: "User",
  is_staff: true,
  organizations: [DEV_ORG],
};

function base64Url(value: object): string {
  return btoa(JSON.stringify(value)).replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
}

// A structurally-valid, non-expired JWT (exp = year 2100) so tokenStorage /
// protectedLoader / isTokenExpired treat the session as live. The signature is
// fake — a real backend would reject it, which is fine for a no-backend dev run.
function fakeJwt(): string {
  return `${base64Url({ alg: "none", typ: "JWT" })}.${base64Url({ exp: 4102444800, sub: "dev-user" })}.dev`;
}

export const DEV_TOKENS: AuthTokens = {
  access: fakeJwt(),
  refresh: fakeJwt(),
};

/** Seed fake tokens into localStorage so the route guards pass. No-op unless bypass is active. */
export function seedDevAuth(): void {
  if (!isDevAuthBypass()) return;
  if (!tokenStorage.get()) {
    tokenStorage.set(DEV_TOKENS);
    console.warn(
      "[devAuth] No real Stytch keys — auto-logged in as dev@local.test (local dev only).",
    );
  }
  // No backend on :8000 in this mode → serve the prototype data from an in-browser mock.
  installDevApiMock();
}
